/* 모임 기록 미리보기 (/ds/travel-journal)
 *
 * 기획서: docs/travel-journal-spec.md
 *
 * 여행·일반 모임을 하면서 그때그때 사진과 글을 남기는 화면입니다.
 * 아직 서버에 붙지 않았습니다. 가짜 데이터는 이 파일에만 둡니다.
 *
 * UX 의 전부는 "마찰" 입니다. 여행 중에 쓰는 글은 귀찮으면 안 씁니다.
 * 그래서 세 가지를 없앴습니다.
 *
 *   1. 장소를 고르지 않는다 — 시각과 일정으로 앱이 이미 압니다(useNowSpot).
 *      그래서 입력창 안내문이 "첨성대에 뭐 남길까요?" 입니다. 어디에 쓰는
 *      글인지 안내문만 읽어도 알 수 있으니 고르는 단계가 사라집니다.
 *   2. 별점을 묻지 않는다 — 접어 둡니다. 일기에 별점은 부담입니다.
 *   3. 글을 강요하지 않는다 — 사진만 올려도 됩니다. 가장 흔한 행동이
 *      가장 짧아야 합니다.
 *
 * 화면 (주소로 고릅니다)
 *   ?tab=home          메인탭 — 여행 진행 중
 *   ?tab=home-normal   메인탭 — 일반 모임 (2차 진행 중)
 *   ?tab=write         남기기 시트
 *   ?tab=anchor        남기기 시트 — 장소 바꾸기
 *   ?tab=feed          모임 기록 — 시간순
 *   ?tab=days          모임 기록 — 일차별
 *   ?tab=empty         아직 기록이 없을 때
 */
import { useSearchParams } from 'react-router';
import {
    AppScreen, Avatar, Badge, Button, ButtonFooter, Chip, EmptyState,
    HeroBanner, Section, SectionHeader, StarRating, Tabs, TextArea,
} from 'components/ds';
import {
    IcCamera, IcChat, IcChevronRight, IcClock, IcHeart, IcPin, IcPlus, IcStarOutline,
} from 'components/common/icons/EmojiIcons';

const IMG = (seed: string) => `https://picsum.photos/seed/${seed}/600/600`;

/* ── 가짜 데이터 ───────────────────────────────────────────── */

interface Entry {
    id: string;
    author: string;
    userNo: number;
    when: string;
    day: number | null;
    place: string;
    text: string;
    photos: string[];
    likes: number;
    liked?: boolean;
    rating?: number;
    comments: { id: string; author: string; text: string }[];
}

const ENTRIES: Entry[] = [
    {
        id: 'e1', author: '민준', userNo: 1, when: '14:20', day: 2, place: '첨성대',
        text: '생각보다 작은데 주변이 넓어서 좋다. 해 지기 전에 오는 게 나을 듯',
        photos: [IMG('cheomseong1'), IMG('cheomseong2')],
        likes: 3, liked: true,
        comments: [
            { id: 'c1', author: '지연', text: '나도 여기 저녁에 다시 가보고 싶어' },
            { id: 'c2', author: '쎈파파', text: '사진 잘 나왔네 ㅋㅋ' },
        ],
    },
    {
        id: 'e2', author: '지연', userNo: 2, when: '12:40', day: 2, place: '황리단길',
        text: '점심 먹은 집 줄이 길었는데 기다릴 만했음',
        photos: [IMG('hwangridan')],
        likes: 5, rating: 5,
        comments: [],
    },
    {
        id: 'e3', author: '쎈파파', userNo: 3, when: '09:15', day: 2, place: '경주 게스트하우스',
        text: '아침 조용하고 좋다',
        photos: [],
        likes: 1,
        comments: [{ id: 'c3', author: '민준', text: '늦잠 잤네요 ㅎㅎ' }],
    },
    {
        id: 'e4', author: '민준', userNo: 1, when: '어제 18:30', day: 1, place: '불국사',
        text: '단풍이 딱 좋았다',
        photos: [IMG('bulguksa1'), IMG('bulguksa2'), IMG('bulguksa3')],
        likes: 7,
        comments: [],
    },
];

/** 메인탭 요약이 쓰는 사진 — 멤버들이 올린 것 중 최근 것 */
const STRIP = [IMG('cheomseong1'), IMG('hwangridan'), IMG('bulguksa1'), IMG('cheomseong2')];
const STRIP_MORE = 9;

/** 남기기 시트에서 장소를 바꿀 때 보여주는 오늘 일정 */
const TODAY_SPOTS = [
    { id: 's1', time: '09:00', name: '경주 게스트하우스', note: '숙소' },
    { id: 's2', time: '11:30', name: '황리단길', note: '' },
    { id: 's3', time: '14:00', name: '첨성대', note: '지금 이곳' },
    { id: 's4', time: '16:30', name: '대릉원', note: '' },
    { id: 's5', time: '19:00', name: '경주 게스트하우스', note: '숙소' },
];

const ME = { userNo: 9, name: '나' };

/* ── 공통 조각 ─────────────────────────────────────────────── */

/**
 * 한 줄 작성기 — 이 기획의 중심.
 *
 * 안내문에 장소 이름이 들어갑니다. 그래야 "여기에 쓰면 첨성대 글이 된다" 는
 * 것을 읽는 순간 알 수 있고, 장소를 고르는 단계가 없어집니다.
 * 누르는 자리는 둘입니다 — 가운데(글) 와 오른쪽 사진 버튼.
 * 메인탭과 모임 상세가 같은 부품을 씁니다.
 */
const Composer = ({ place }: { place: string }) => (
    <div
        style={{
            display: 'flex', alignItems: 'center', gap: 'var(--t-space-3)',
            padding: 'var(--t-space-3)',
            border: '1px solid var(--t-line)',
            borderRadius: 'var(--t-radius-md)',
            background: 'var(--t-surface)',
        }}
    >
        <Avatar person={{ userNo: ME.userNo, name: ME.name }} size="sm" />
        <span
            style={{
                flex: 1, minWidth: 0, color: 'var(--t-ink-3)',
                fontSize: 'var(--t-font-body-sm)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
        >
            {place}에 뭐 남길까요?
        </span>
        {/* 사진이 주 재료라 바로 누를 수 있는 자리를 따로 둡니다 */}
        <span
            style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 34, height: 34, flex: '0 0 34px',
                border: '1px solid var(--t-line)',
                borderRadius: 'var(--t-radius-sm)',
                color: 'var(--t-ink-2)',
            }}
        >
            <IcCamera size={17} />
        </span>
    </div>
);

/** 메인탭 요약 — 사진 줄 + 최근 글 한 줄 + 작성기 */
const NORMAL_LATEST = { author: '태현', place: '역삼 고깃집', text: '1차 고기 미쳤다 사장님 인심 좋으심' };

const JournalSummary = ({ place, newCount, normal }: {
    place: string; newCount: number; normal?: boolean;
}) => {
    const latest = normal ? NORMAL_LATEST : ENTRIES[0];
    return (
        <Section spacing="tight">
            <SectionHeader
                title="우리 기록"
                sub={`사진 ${STRIP.length + STRIP_MORE}장 · 글 ${ENTRIES.length}개`}
            />

            {/* 사진 줄 — 누르면 모임 기록으로 들어갑니다 */}
            <div style={{ display: 'flex', gap: 'var(--t-space-2)', marginTop: 'var(--t-space-3)' }}>
                {STRIP.map((url, i) => (
                    <span
                        key={url}
                        style={{
                            position: 'relative', flex: 1, aspectRatio: '1 / 1',
                            borderRadius: 'var(--t-radius-sm)', overflow: 'hidden',
                            background: 'var(--t-surface-muted)',
                        }}
                    >
                        <img
                            src={url} alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                        {/* 마지막 칸에 남은 장수를 겹칩니다 — 칸을 더 늘리지 않으려고 */}
                        {i === STRIP.length - 1 && (
                            <span
                                style={{
                                    position: 'absolute', inset: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: 'rgba(0,0,0,.52)', color: '#fff',
                                    fontSize: 'var(--t-font-body)', fontWeight: 'var(--t-weight-bold)' as any,
                                }}
                            >
                                +{STRIP_MORE}
                            </span>
                        )}
                    </span>
                ))}
            </div>

            {/* 최근 글 한 줄 — 누구의 어느 장소 글인지까지만 */}
            <div
                style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--t-space-2)',
                    marginTop: 'var(--t-space-3)',
                }}
            >
                <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-font-body-sm)', color: 'var(--t-ink-2)' }}>
                    <b style={{ fontWeight: 'var(--t-weight-medium)' as any, color: 'var(--t-ink-1)' }}>
                        {latest.author}
                    </b>
                    {' · '}{latest.place} —{' '}
                    <span
                        style={{
                            display: 'inline-block', maxWidth: 148, verticalAlign: 'bottom',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                    >
                        {latest.text}
                    </span>
                </span>
                {newCount > 0 && <Badge tone="brand">새 글 {newCount}</Badge>}
                <span style={{ color: 'var(--t-ink-3)', display: 'inline-flex' }}><IcChevronRight size={16} /></span>
            </div>

            <div style={{ marginTop: 'var(--t-space-3)' }}>
                <Composer place={place} />
            </div>
        </Section>
    );
};

/** 기록 한 건 — 피드의 카드 */
const EntryCard = ({ entry, openComments, hideDay }: {
    entry: Entry; openComments?: boolean;
    /** 일차별 보기 — Day 제목이 이미 말하고 있어 머리에서는 뺍니다 */
    hideDay?: boolean;
}) => (
    <article
        style={{
            padding: 'var(--t-space-4) 0',
            borderTop: '1px solid var(--t-line)',
        }}
    >
        {/* 머리 — 누가 · 어디 · 언제. 장소를 여기 붙여야 사진만 있는 글도 읽힙니다 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--t-space-2)' }}>
            <Avatar person={{ userNo: entry.userNo, name: entry.author }} size="sm" />
            <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 'var(--t-font-body-sm)', fontWeight: 'var(--t-weight-medium)' as any }}>
                    {entry.author}
                </span>
                <span
                    style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)',
                    }}
                >
                    <IcPin size={11} />
                    {entry.day && !hideDay ? `${entry.day}일차 · ` : ''}{entry.place}
                    <span style={{ margin: '0 2px' }}>·</span>
                    {entry.when}
                </span>
            </span>
            {entry.rating ? <StarRating value={entry.rating} size={13} source="CREW" /> : null}
        </div>

        {/* 사진 — 한 장은 크게, 여러 장은 가로로 넘깁니다 */}
        {entry.photos.length > 0 && (
            <div
                style={{
                    display: 'flex', gap: 'var(--t-space-2)',
                    marginTop: 'var(--t-space-3)',
                    overflowX: 'auto',
                }}
            >
                {entry.photos.map(url => (
                    <img
                        key={url} src={url} alt=""
                        style={{
                            /* min-width 를 0 으로 눌러야 합니다 — flex 항목의 자동 최소폭은
                               이미지 원본 폭이라, 안 누르면 600px 짜리가 줄을 밀어냅니다. */
                            flex: entry.photos.length === 1 ? '1 1 100%' : '0 0 46%',
                            minWidth: 0,
                            height: entry.photos.length === 1 ? 230 : 160,
                            objectFit: 'cover', display: 'block',
                            borderRadius: 'var(--t-radius-sm)', background: 'var(--t-surface-muted)',
                        }}
                    />
                ))}
            </div>
        )}

        {entry.text && (
            <p
                style={{
                    margin: 'var(--t-space-3) 0 0',
                    fontSize: 'var(--t-font-body-sm)',
                    lineHeight: 'var(--t-leading-normal)',
                    color: 'var(--t-ink-1)',
                }}
            >
                {entry.text}
            </p>
        )}

        {/* 좋아요·댓글 — 숫자만. 누르면 댓글이 펼쳐집니다 */}
        <div
            style={{
                display: 'flex', alignItems: 'center', gap: 'var(--t-space-4)',
                marginTop: 'var(--t-space-3)',
                fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)',
            }}
        >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: entry.liked ? 'var(--t-brand)' : undefined }}>
                <IcHeart size={14} /> {entry.likes}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <IcChat size={14} /> {entry.comments.length}
            </span>
        </div>

        {openComments && entry.comments.length > 0 && (
            <ul
                style={{
                    listStyle: 'none', margin: 'var(--t-space-3) 0 0', padding: 'var(--t-space-3)',
                    background: 'var(--t-surface-muted)', borderRadius: 'var(--t-radius-sm)',
                    display: 'flex', flexDirection: 'column', gap: 'var(--t-space-2)',
                }}
            >
                {entry.comments.map(c => (
                    <li key={c.id} style={{ fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-2)' }}>
                        <b style={{ fontWeight: 'var(--t-weight-medium)' as any, color: 'var(--t-ink-1)' }}>{c.author}</b>
                        {'  '}{c.text}
                    </li>
                ))}
                <li style={{ marginTop: 'var(--t-space-1)' }}>
                    <span
                        style={{
                            display: 'block', padding: 'var(--t-space-2) var(--t-space-3)',
                            background: 'var(--t-surface)', border: '1px solid var(--t-line)',
                            borderRadius: 'var(--t-radius-sm)',
                            fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)',
                        }}
                    >
                        댓글 남기기
                    </span>
                </li>
            </ul>
        )}
    </article>
);

/* ── 화면 ──────────────────────────────────────────────────── */

/** 메인탭 — 오늘 카드 아래에 우리 기록이 붙습니다 */
const HomeScreen = ({ normal }: { normal?: boolean }) => (
    <>
        <HeroBanner
            imageUrl={IMG(normal ? 'yeoksam' : 'gyeongju')}
            photos={[IMG(normal ? 'yeoksam' : 'gyeongju')]}
            tags={[{ label: normal ? '일반' : '여행', tone: 'brand' }]}
            title={normal ? '금요일 회식' : '경주 2박3일'}
            paragraph={normal ? '역삼 · 6명' : '경주 · 4명'}
            date={normal ? '오늘 오후 7:00' : '2일차 · 09/28 (월)'}
        />

        {/* 지금 어디인지 — 기존 오늘 카드가 이미 보여주던 것 */}
        <Section spacing="tight">
            <div
                style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--t-space-2)',
                    padding: 'var(--t-space-3)',
                    background: 'var(--t-surface-muted)',
                    borderRadius: 'var(--t-radius-md)',
                    fontSize: 'var(--t-font-body-sm)',
                }}
            >
                <span style={{ color: 'var(--t-brand)', display: 'inline-flex' }}><IcClock size={15} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                    <b style={{ fontWeight: 'var(--t-weight-medium)' as any }}>
                        {normal ? '2차 · 역삼 포차' : '지금 이곳 · 첨성대'}
                    </b>
                    <span style={{ color: 'var(--t-ink-3)' }}>
                        {normal ? '  오후 9:00 이동' : '  오후 2:00'}
                    </span>
                </span>
            </div>
        </Section>

        <JournalSummary place={normal ? '역삼 포차' : '첨성대'} newCount={normal ? 1 : 3} normal={normal} />
    </>
);

/** 남기기 시트 — 앵커가 이미 정해져 있어 바로 쓰기만 하면 됩니다 */
const WriteScreen = ({ pickAnchor }: { pickAnchor?: boolean }) => (
    <>
        <Section spacing="tight">
            {/* 앵커 — 앱이 정한 값을 보여주고, 틀렸을 때만 손대게 합니다 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--t-space-2)' }}>
                <Chip label="2일차 · 첨성대" selected />
                <span style={{ fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)' }}>
                    {pickAnchor ? '어디 기록인가요?' : '장소 바꾸기'}
                </span>
            </div>

            {pickAnchor && (
                <ul
                    style={{
                        listStyle: 'none', margin: 'var(--t-space-3) 0 0', padding: 0,
                        border: '1px solid var(--t-line)', borderRadius: 'var(--t-radius-md)',
                        overflow: 'hidden',
                    }}
                >
                    {TODAY_SPOTS.map((s, i) => (
                        <li
                            key={s.id}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 'var(--t-space-3)',
                                padding: 'var(--t-space-3)',
                                borderTop: i === 0 ? 'none' : '1px solid var(--t-line)',
                                background: s.note === '지금 이곳' ? 'var(--t-brand-weak)' : undefined,
                            }}
                        >
                            <span
                                style={{
                                    flex: '0 0 44px', fontSize: 'var(--t-font-caption)',
                                    color: 'var(--t-ink-3)', fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {s.time}
                            </span>
                            <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-font-body-sm)' }}>{s.name}</span>
                            {s.note && (
                                <span style={{ fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)' }}>{s.note}</span>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </Section>

        {!pickAnchor && (
            <>
                {/* 사진 — 첫 칸이 추가 버튼. 사진만 올려도 등록됩니다 */}
                <Section spacing="tight">
                    <div style={{ display: 'flex', gap: 'var(--t-space-2)' }}>
                        <span
                            style={{
                                display: 'inline-flex', flexDirection: 'column',
                                alignItems: 'center', justifyContent: 'center', gap: 2,
                                flex: '0 0 78px', aspectRatio: '1 / 1',
                                border: '1px dashed var(--t-line-strong)',
                                borderRadius: 'var(--t-radius-sm)',
                                color: 'var(--t-ink-3)', fontSize: 'var(--t-font-caption)',
                            }}
                        >
                            <IcPlus size={18} />
                            사진
                        </span>
                        {[IMG('cheomseong1'), IMG('cheomseong2')].map(url => (
                            <img
                                key={url} src={url} alt=""
                                style={{
                                    flex: '0 0 78px', minWidth: 0,
                                    width: 78, height: 78, objectFit: 'cover',
                                    borderRadius: 'var(--t-radius-sm)', display: 'block',
                                    background: 'var(--t-surface-muted)',
                                }}
                            />
                        ))}
                    </div>
                </Section>

                <Section spacing="tight">
                    <TextArea
                        label="한마디"
                        optionalText="안 써도 괜찮아요"
                        value="생각보다 작은데 주변이 넓어서 좋다"
                        onChange={() => undefined}
                        rows={4}
                        maxLength={500}
                        showCount
                    />
                </Section>

                {/* 별점은 접어 둡니다 — 일기에 별점을 물으면 손이 멈춥니다 */}
                <Section spacing="tight">
                    <div
                        style={{
                            display: 'flex', alignItems: 'center', gap: 'var(--t-space-2)',
                            padding: 'var(--t-space-3)',
                            border: '1px solid var(--t-line)', borderRadius: 'var(--t-radius-md)',
                            color: 'var(--t-ink-2)', fontSize: 'var(--t-font-body-sm)',
                        }}
                    >
                        <IcStarOutline size={16} />
                        <span style={{ flex: 1 }}>별점도 남기기</span>
                        <span style={{ fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)' }}>선택</span>
                    </div>
                </Section>
            </>
        )}

        <ButtonFooter>
            <Button variant="primary" size="lg" block>{pickAnchor ? '이 장소로' : '남기기'}</Button>
        </ButtonFooter>
    </>
);

/** 모임 기록 — 시간순. 여행 중에는 이게 자연스럽습니다 */
const FeedScreen = () => (
    <>
        <Section spacing="tight">
            <Tabs
                items={[{ value: 'time', label: '시간순' }, { value: 'day', label: '일차별' }]}
                value="time"
                onChange={() => undefined}
                ariaLabel="보기 방식"
            />
        </Section>

        {/* 피드 맨 위에 작성기 — 읽다가 바로 쓸 수 있어야 합니다 */}
        <Section spacing="tight">
            <Composer place="첨성대" />
        </Section>

        <Section>
            {ENTRIES.map((e, i) => (
                <EntryCard key={e.id} entry={e} openComments={i === 0} />
            ))}
        </Section>
    </>
);

/** 모임 기록 — 일차별. 여행이 끝난 뒤 정리된 기록으로 읽힙니다 */
const DaysScreen = () => {
    const byDay = [2, 1].map(d => ({ day: d, items: ENTRIES.filter(e => e.day === d) }));
    return (
        <>
            <Section spacing="tight">
                <Tabs
                    items={[{ value: 'time', label: '시간순' }, { value: 'day', label: '일차별' }]}
                    value="day"
                    onChange={() => undefined}
                    ariaLabel="보기 방식"
                />
            </Section>

            {byDay.map(({ day, items }) => (
                <Section key={day} divided>
                    <SectionHeader
                        title={`Day ${day}`}
                        sub={day === 2 ? '09/28 (월) · 글 3개' : '09/27 (일) · 글 1개'}
                    />
                    {items.map(e => <EntryCard key={e.id} entry={e} hideDay />)}
                </Section>
            ))}
        </>
    );
};

/** 아직 기록이 없을 때 — 첫 글을 남기는 자리만 남깁니다 */
const EmptyScreen = () => (
    <>
        <Section spacing="tight">
            <Composer place="첨성대" />
        </Section>
        <Section>
            <EmptyState
                icon={<IcCamera size={22} />}
                title="아직 기록이 없어요"
                body="사진 한 장만 올려도 됩니다. 여행이 끝난 뒤 이 기록이 그대로 우리 앨범이 됩니다."
            />
        </Section>
    </>
);

/* ── 셸 ────────────────────────────────────────────────────── */

type Tab = 'home' | 'home-normal' | 'write' | 'anchor' | 'feed' | 'days' | 'empty';

const TITLES: Record<Tab, string> = {
    home: 'DOKOCREW',
    'home-normal': 'DOKOCREW',
    write: '기록 남기기',
    anchor: '기록 남기기',
    feed: '우리 기록',
    days: '우리 기록',
    empty: '우리 기록',
};

export const TravelJournalPreview = () => {
    const [params] = useSearchParams();
    const raw = params.get('tab') as Tab | null;
    const tab: Tab = raw && raw in TITLES ? raw : 'home';
    const isHome = tab === 'home' || tab === 'home-normal';

    return (
        <AppScreen
            title={TITLES[tab]}
            bar={isHome ? 'plain' : 'sub'}
            gnb={isHome}
            hasFooter={tab === 'write' || tab === 'anchor'}
        >
            {tab === 'home' && <HomeScreen />}
            {tab === 'home-normal' && <HomeScreen normal />}
            {tab === 'write' && <WriteScreen />}
            {tab === 'anchor' && <WriteScreen pickAnchor />}
            {tab === 'feed' && <FeedScreen />}
            {tab === 'days' && <DaysScreen />}
            {tab === 'empty' && <EmptyScreen />}
        </AppScreen>
    );
};
